import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@finitless/shared/lib";
import { buildQueryParams } from "../lib/globalUtils";

const CAMPAIGNS_QUERY_KEY = "campaigns";

/**
 * Build where clause for campaigns filtering
 */
const buildCampaignWhereClause = (
  contactType = "all",
  status = "all",
  searchTerm = "",
) => {
  const conditions = [];

  if (contactType && contactType !== "all") {
    conditions.push({
      attribute: "contact_type",
      value: contactType,
    });
  }

  if (status && status !== "all") {
    conditions.push({
      attribute: "status",
      value: status,
    });
  }

  if (searchTerm) {
    conditions.push({
      conector: "OR",
      conditions: [
        {
          attribute: "name",
          value: {
            condition: "ilike",
            value: `%${searchTerm}%`,
          },
        },
        {
          attribute: "contact_type",
          value: {
            condition: "ilike",
            value: `%${searchTerm}%`,
          },
        },
      ],
    });
  }

  if (conditions.length === 0) return null;

  return {
    conditions,
    conector: "AND",
  };
};

/**
 * Hook to get campaigns list with pagination and filtering
 */
export const useCampaigns = (params = {}) => {
  const {
    contactType = "all",
    status = "all",
    searchTerm = "",
    page = 1,
    pageSize = 10,
    enabled = true,
  } = params;

  const whereClause = buildCampaignWhereClause(contactType, status, searchTerm);

  const queryParams = {
    page,
    page_size: pageSize,
    orderBy: "-id",
    ...(whereClause && { where: JSON.stringify(whereClause) }),
  };

  const queryString = buildQueryParams(queryParams);

  return useQuery({
    queryKey: [
      CAMPAIGNS_QUERY_KEY,
      contactType,
      status,
      searchTerm,
      page,
      pageSize,
    ],
    queryFn: async () => {
      const response = await apiClient.get(
        `/marketing_campaigns${queryString ? `?${queryString}` : ""}`,
      );
      return {
        campaigns: response?.result || [],
        pagination: response?.pagination || {},
      };
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to get a single campaign by ID
 */
export const useCampaignDetail = (campaignId, initialData = null) => {
  return useQuery({
    queryKey: [CAMPAIGNS_QUERY_KEY, "detail", campaignId],
    queryFn: async () => {
      const response = await apiClient.get(
        `/marketing_campaigns/${campaignId}`,
      );
      return response?.result || null;
    },
    enabled: !!campaignId,
    initialData,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to create a new campaign
 */
export const useCreateCampaign = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (campaignData) => {
      const response = await apiClient.post(
        "/marketing_campaigns",
        campaignData,
      );
      return response?.result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CAMPAIGNS_QUERY_KEY] });
    },
    retry: false,
  });
};

/**
 * Hook to update a campaign
 */
export const useUpdateCampaign = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ campaignId, data }) => {
      const response = await apiClient.put(
        `/marketing_campaigns/${campaignId}`,
        data,
      );
      return response?.result;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [CAMPAIGNS_QUERY_KEY] });
      queryClient.invalidateQueries({
        queryKey: [CAMPAIGNS_QUERY_KEY, "detail", variables.campaignId],
      });
    },
  });
};

/**
 * Hook to delete a campaign
 */
export const useDeleteCampaign = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (campaignId) => {
      const response = await apiClient.delete(
        `/marketing_campaigns/${campaignId}`,
      );
      return response?.result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CAMPAIGNS_QUERY_KEY] });
    },
  });
};

/**
 * Hook to toggle campaign enabled status
 */
export const useToggleCampaignStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ campaignId, enabled }) => {
      const response = await apiClient.put(
        `/marketing_campaigns/${campaignId}`,
        { enabled },
      );
      return response?.result;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [CAMPAIGNS_QUERY_KEY] });
      queryClient.invalidateQueries({
        queryKey: [CAMPAIGNS_QUERY_KEY, "detail", variables.campaignId],
      });
    },
  });
};

/**
 * Hook to get campaign audience count
 */
export const useCampaignAudience = (conditions = [], contactType = "") => {
  const conditionsKey = JSON.stringify(conditions);

  return useQuery({
    queryKey: [CAMPAIGNS_QUERY_KEY, "audience", conditionsKey, contactType],
    queryFn: async () => {
      // Remove null values from conditions (same as base repo)
      const cleanConditions = conditions.map((condition) => {
        const cleaned = {};
        Object.keys(condition).forEach((key) => {
          if (condition[key] !== null) {
            cleaned[key] = condition[key];
          }
        });
        return cleaned;
      });

      const response = await apiClient.post("/marketing_campaigns/audience", {
        conditions: JSON.stringify(cleanConditions),
        contact_type: contactType,
      });
      return {
        audience: response?.result || 0,
        pagination: response?.pagination || {},
      };
    },
    enabled: conditions.length > 0 && !!contactType,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export default {
  useCampaigns,
  useCampaignDetail,
  useCreateCampaign,
  useUpdateCampaign,
  useDeleteCampaign,
  useToggleCampaignStatus,
  useCampaignAudience,
};
